import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';
import { Loader2, CheckCircle, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    if (password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password,
      });
      setSuccess(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restablecer contraseña');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-8">
        <div className="text-center">
          <h1 className="font-heading text-2xl text-white mb-4">ENLACE INVÁLIDO</h1>
          <p className="text-zinc-400 mb-6">Este enlace no es válido o ha expirado.</p>
          <Link to="/forgot-password">
            <Button className="bg-primary hover:bg-primary/90">Solicitar nuevo enlace</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-8">
      <div className="noise-overlay" />
      <div className="w-full max-w-md relative z-10" data-testid="reset-password-page">
        <div className="flex items-center justify-center gap-3 mb-8">
          <img src="/logo-ar.png" alt="AR Performance" className="w-10 h-10 object-contain" />
          <span className="font-heading text-xl font-bold text-white">AR PERFORMANCE</span>
        </div>

        {success ? (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-white">CONTRASEÑA ACTUALIZADA</h1>
            <p className="text-zinc-400">Tu contraseña se ha restablecido correctamente.</p>
            <Link to="/login">
              <Button className="mt-4 bg-primary hover:bg-primary/90 text-white">
                Iniciar Sesión
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <div className="text-center mb-8">
              <h1 className="font-heading text-3xl font-bold text-white mb-2">NUEVA CONTRASEÑA</h1>
              <p className="text-zinc-400">Introduce tu nueva contraseña</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-zinc-300">Nueva Contraseña</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="bg-surface-highlight border-white/10 text-white placeholder:text-zinc-600 h-12 pr-12"
                    data-testid="reset-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-zinc-300">Confirmar Contraseña</Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="bg-surface-highlight border-white/10 text-white placeholder:text-zinc-600 h-12"
                  data-testid="reset-confirm-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-primary hover:bg-primary/90 text-white font-heading uppercase tracking-wider h-12"
                data-testid="reset-submit-btn"
              >
                {loading ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Restableciendo...</>
                ) : (
                  'Restablecer Contraseña'
                )}
              </Button>
            </form>
          </>
        )}
      </div>
      <Toaster position="top-right" richColors />
    </div>
  );
};

export default ResetPasswordPage;
